import type {
  Cycle,
  CreateCycleInput,
  CreateMilestoneInput,
  CreateProjectInput,
  Project,
  ProjectMilestone,
  Task,
  TaskDependency,
  UpdateCycleInput,
  UpdateMilestoneInput,
  UpdateProjectInput,
} from "@open-sunsama/types";
import type { OpenSunsamaClient, RequestOptions } from "./client.js";

interface ApiResponse<T> {
  success: boolean;
  data: T;
}
export type ProjectDetail = Project & {
  milestones: ProjectMilestone[];
  tasks: Task[];
};
export type CycleDetail = Cycle & { tasks: Task[] };
export type TaskDependencyDetail = TaskDependency & {
  blockerTitle: string;
  blockerCompletedAt: Date | null;
};

export interface ProjectsApi {
  list(options?: RequestOptions): Promise<Project[]>;
  get(id: string, options?: RequestOptions): Promise<ProjectDetail>;
  create(input: CreateProjectInput, options?: RequestOptions): Promise<Project>;
  update(
    id: string,
    input: UpdateProjectInput,
    options?: RequestOptions
  ): Promise<Project>;
  delete(id: string, options?: RequestOptions): Promise<void>;
  milestones: {
    create(
      projectId: string,
      input: CreateMilestoneInput,
      options?: RequestOptions
    ): Promise<ProjectMilestone>;
    update(
      projectId: string,
      milestoneId: string,
      input: UpdateMilestoneInput,
      options?: RequestOptions
    ): Promise<ProjectMilestone>;
    delete(
      projectId: string,
      milestoneId: string,
      options?: RequestOptions
    ): Promise<void>;
  };
}

export interface CyclesApi {
  list(options?: RequestOptions): Promise<Cycle[]>;
  get(id: string, options?: RequestOptions): Promise<CycleDetail>;
  create(input: CreateCycleInput, options?: RequestOptions): Promise<Cycle>;
  update(
    id: string,
    input: UpdateCycleInput,
    options?: RequestOptions
  ): Promise<Cycle>;
  delete(id: string, options?: RequestOptions): Promise<void>;
}

export interface TaskDependenciesApi {
  list(
    taskId: string,
    options?: RequestOptions
  ): Promise<TaskDependencyDetail[]>;
  create(
    taskId: string,
    dependsOnTaskId: string,
    options?: RequestOptions
  ): Promise<TaskDependency>;
  delete(
    taskId: string,
    dependencyId: string,
    options?: RequestOptions
  ): Promise<void>;
}

export function createProjectsApi(client: OpenSunsamaClient): ProjectsApi {
  return {
    async list(options) {
      return (
        (await client.get<ApiResponse<Project[]>>("projects", options)).data ??
        []
      );
    },
    async get(id, options) {
      return (
        await client.get<ApiResponse<ProjectDetail>>(`projects/${id}`, options)
      ).data;
    },
    async create(input, options) {
      return (
        await client.post<ApiResponse<Project>>("projects", input, options)
      ).data;
    },
    async update(id, input, options) {
      return (
        await client.patch<ApiResponse<Project>>(
          `projects/${id}`,
          input,
          options
        )
      ).data;
    },
    async delete(id, options) {
      await client.delete(`projects/${id}`, options);
    },
    milestones: {
      async create(projectId, input, options) {
        return (
          await client.post<ApiResponse<ProjectMilestone>>(
            `projects/${projectId}/milestones`,
            input,
            options
          )
        ).data;
      },
      async update(projectId, milestoneId, input, options) {
        return (
          await client.patch<ApiResponse<ProjectMilestone>>(
            `projects/${projectId}/milestones/${milestoneId}`,
            input,
            options
          )
        ).data;
      },
      async delete(projectId, milestoneId, options) {
        await client.delete(
          `projects/${projectId}/milestones/${milestoneId}`,
          options
        );
      },
    },
  };
}

export function createCyclesApi(client: OpenSunsamaClient): CyclesApi {
  return {
    async list(options) {
      return (
        (await client.get<ApiResponse<Cycle[]>>("cycles", options)).data ?? []
      );
    },
    async get(id, options) {
      return (
        await client.get<ApiResponse<CycleDetail>>(`cycles/${id}`, options)
      ).data;
    },
    async create(input, options) {
      return (await client.post<ApiResponse<Cycle>>("cycles", input, options))
        .data;
    },
    async update(id, input, options) {
      return (
        await client.patch<ApiResponse<Cycle>>(`cycles/${id}`, input, options)
      ).data;
    },
    async delete(id, options) {
      await client.delete(`cycles/${id}`, options);
    },
  };
}

export function createTaskDependenciesApi(
  client: OpenSunsamaClient
): TaskDependenciesApi {
  return {
    async list(taskId, options) {
      return (
        (
          await client.get<ApiResponse<TaskDependencyDetail[]>>(
            `tasks/${taskId}/dependencies`,
            options
          )
        ).data ?? []
      );
    },
    async create(taskId, dependsOnTaskId, options) {
      return (
        await client.post<ApiResponse<TaskDependency>>(
          `tasks/${taskId}/dependencies`,
          { dependsOnTaskId },
          options
        )
      ).data;
    },
    async delete(taskId, dependencyId, options) {
      await client.delete(
        `tasks/${taskId}/dependencies/${dependencyId}`,
        options
      );
    },
  };
}
